import React, { useEffect, useState } from 'react'
import {
    HomeIcon,
    TruckIcon,
    DocumentReportIcon,
    CalendarIcon,
    UserGroupIcon,
    SearchIcon,
    ChatAlt2Icon,
    ChevronRightIcon,
    XIcon,
    MenuAlt3Icon,
    ClipboardIcon,
} from '@heroicons/react/outline'
import { useNetworkStatus } from '../../utils/networkStatus'
import clsx from 'clsx'
import L1NLogoWhiteStroke from '../../svg/L1N-logo-white-stroke.svg'
import L1SmallLogoWhiteStroke from '../../svg/L1N-whitestroke-small-logo.svg'
import NewFeatureBadge from '../../svg/NewFeatureBadge.svg'
import useAuth from '../../hooks/useAuth'
import AppRoutes from '../../routes/appRoutes'
import { NavLink } from 'react-router-dom'
import useScreen from '../../hooks/useScreen'
import ClickAwayListener from 'react-click-away-listener'
import Warning from '../../svg/Warning'
import { isPwaInstalled } from '../../utils/isPwaInstalled'
import { removeStepsAndRequests } from '../../utils/handleOnDone'

const getNavItems = (isOffline, isOpen, pwaIsInstalled, showUsedEquipment) => {
    const equipmentName = showUsedEquipment
        ? 'Equipment Finder'
        : 'Equipment Quote Request'
    const equipmentLink = showUsedEquipment
        ? AppRoutes.equipmentFinder()
        : AppRoutes.newEquipmentRequest()

    const supportLinkSelected = window.location?.pathname?.includes('support')
    const navItems = [
        {
            name: 'Dashboard',
            link: AppRoutes.dashboard(),
            icon: function icon(className) {
                return <HomeIcon className={className} />
            },
            isDisable: false,
        },
        {
            name: 'Fleet',
            link: AppRoutes.fleet(),
            icon: function icon(className) {
                return <TruckIcon className={className} />
            },
            isDisable: false,
        },
        {
            name: 'Requests',
            link: AppRoutes.requests() + '?activeTab=activeRequests',
            icon: function icon(className) {
                return <DocumentReportIcon className={className} />
            },
            isDisable: false,
        },
        {
            name: 'Planned Maintenance',
            link: AppRoutes.plannedMaintenance() + '?activeTab=activeRequests',
            icon: function icon(className) {
                return <CalendarIcon className={className} />
            },
            isDisable: false,
        },
        {
            name: equipmentName,
            link: equipmentLink,
            icon: function icon(className) {
                return <SearchIcon className={className} />
            },
            isDisable: isOffline,
        },
        {
            name: 'Rental Request',
            link: AppRoutes.rentalRequests(),
            icon: function icon(className) {
                return <ClipboardIcon className={className} />
            },
            isDisable: isOffline,
        },
        {
            name: 'Users',
            link: AppRoutes.users(),
            icon: function icon(className) {
                return <UserGroupIcon className={className} />
            },
            isDisable: isOffline,
        },
        {
            name: 'Support',
            link: AppRoutes.supportPage(),
            icon: function icon(className) {
                return (
                    <div className="relative">
                        <ChatAlt2Icon className={className} />
                        {!pwaIsInstalled && !isOpen && !supportLinkSelected && (
                            <div
                                className="absolute bottom-3.5 left-3.5 w-2 h-2 bg-yellow rounded-full"
                                aria-label="Notification"
                            ></div>
                        )}
                    </div>
                )
            },
            isDisable: false,
        },
    ]
    return navItems
}

const Sidebar = () => {
    const { isMobile, isSmallScreen } = useScreen()
    const pwaIsInstalled = isPwaInstalled()

    const isDesktop = !isMobile
    const [isOpen, setIsOpen] = useState(isDesktop)
    const [isExpanded, setIsExpanded] = useState(false)
    const [isHover, setIsHover] = useState(false)

    // This padding is to allow the banner to be displayed without meesing the structure of the sidebar
    const [hasPadding, setHasPadding] = useState(false)

    const { user, impersonated: isImpersonated } = useAuth()
    const userCompanyName = user?.company?.name
    const firstName = user?.first_name
    const lastName = user?.last_name

    const userFeatureFlags = user?.feature_flags
    const showUsedEquipment = userFeatureFlags.find(
        flag => flag.feature == 'usedEquipment',
    )?.active

    const { isOffline } = useNetworkStatus()
    const navItems = getNavItems(
        isOffline,
        isOpen,
        pwaIsInstalled,
        showUsedEquipment,
    )

    const acronymName = `${firstName[0]?.toUpperCase()}${lastName[0]?.toUpperCase()}`
    let timeOut = 0
    const isInProfilePage = window.location?.pathname?.includes('profile')

    const path = window.location.pathname

    const toggleOpen = () => {
        setIsOpen(!isOpen)
    }

    useEffect(() => {
        if (isOffline) {
            setHasPadding(true)
        } else {
            // This timeout matches the same one the banner, if change this one also change on NetworkStatusAlert file
            const paddingTimeout = setTimeout(() => {
                console.log('After 3 seconds we remove padding to adjust')
                setHasPadding(false)
            }, 3000)
            return () => clearTimeout(paddingTimeout)
        }
    }, [isOffline])

    useEffect(() => {
        if (isMobile && isExpanded) {
            setIsExpanded(false)
        }
    }, [path])

    const toggleExpanded = () => {
        setIsExpanded(!isExpanded)
    }

    const onMouseEnter = () => {
        if (!isOpen) {
            timeOut = setTimeout(() => {
                setIsHover(true)
            }, [500])
        } else {
            if (!isHover) {
                setIsHover(true)
            }
        }
    }

    const onMouseLeave = () => {
        clearTimeout(timeOut)
        setIsHover(false)
    }

    return (
        <ClickAwayListener
            onClickAway={() => (isMobile ? setIsExpanded(false) : null)}
        >
            <div
                className={clsx(
                    isDesktop && 'min-h-full h-auto relative',
                    'transition-all duration-150 flex flex-col items-center justify-center z-150 md:z-auto',
                    isOpen &&
                        isDesktop &&
                        'md:w-68 md:max-w-68 md:min-w-68 w-68',
                    !isOpen && isDesktop && 'w-24 min-w-24 max-w-24',
                    !isOpen && isDesktop && isHover && 'w-68 min-w-68 max-w-68',
                    !isDesktop &&
                        clsx(
                            'fixed left-0 bg-black80',
                            isExpanded
                                ? 'w-80 h-auto min-h-full'
                                : 'w-full h-14',
                            hasPadding ? 'top-6.5' : 'top-0',
                        ),
                )}
            >
                <div
                    role="button"
                    onClick={toggleOpen}
                    className={clsx(
                        'md:flex hidden  z-170 cursor-pointer bg-black60 shadow rounded-full items-center justify-center absolute top-6 -right-2.5 duration-300 transition-opacity border border-black50',
                        isSmallScreen ? 'w-6 h-6' : 'w-6 h-6',
                        !isOpen && 'opacity-100',
                        isHover && 'opacity-100',
                    )}
                >
                    <ChevronRightIcon
                        className={clsx(
                            'w-3 h-3 text-white stroke-current stroke-2 transition-all transform',
                            isOpen && 'rotate-180',
                        )}
                    />
                </div>
                {isMobile && (
                    <div className="w-full items-center justify-between md:hidden flex p-4">
                        <NavLink to={AppRoutes.home()}>
                            <img
                                className={clsx('h-6 transition-all')}
                                alt="LiftOne Now Logo"
                                src={L1NLogoWhiteStroke}
                            />
                        </NavLink>

                        {isExpanded ? (
                            <XIcon
                                onClick={toggleExpanded}
                                className="text-white w-6 h-6 stroke-current"
                            />
                        ) : (
                            <div className="relative">
                                <MenuAlt3Icon
                                    onClick={toggleExpanded}
                                    className="text-white w-6 h-6 stroke-current"
                                />
                                {!pwaIsInstalled && (
                                    <div
                                        className="absolute top-0 right-0 w-2 h-2 bg-yellow rounded-full"
                                        aria-label="Notification"
                                    ></div>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {(isDesktop || isExpanded) && (
                    <div
                        onMouseEnter={onMouseEnter}
                        onMouseLeave={onMouseLeave}
                        className={clsx(
                            isDesktop && 'group',
                            'transition-all z-150 w-full h-full flex-grow flex flex-col items-center justify-between bg-black80 overflow-hidden',
                        )}
                    >
                        {isDesktop && (
                            <NavLink
                                className={
                                    !isOpen && !isHover
                                        ? 'hidden '
                                        : 'md:block hidden'
                                }
                                to={AppRoutes.home()}
                            >
                                <img
                                    className={clsx(
                                        ' h-auto transition-all',
                                        isSmallScreen
                                            ? 'w-32 my-6'
                                            : 'w-36 my-6',
                                    )}
                                    alt="LiftOne Now Logo"
                                    src={L1NLogoWhiteStroke}
                                />
                            </NavLink>
                        )}

                        {isDesktop && (
                            <NavLink
                                className={
                                    !isOpen && !isHover
                                        ? 'md:block hidden '
                                        : 'hidden'
                                }
                                to={AppRoutes.home()}
                            >
                                <img
                                    className={clsx(
                                        'h-auto transition-all',
                                        isSmallScreen
                                            ? 'w-16 mt-6'
                                            : 'w-16 mt-6',
                                    )}
                                    alt="LiftOne Now Small Logo"
                                    src={L1SmallLogoWhiteStroke}
                                />
                            </NavLink>
                        )}
                        {/* this is for generate the space between correctly */}
                        <div className="md:hidden block h-2" />
                        <nav
                            className={clsx(
                                'w-full h-auto md:mt-0 mt-12 flex flex-col items-start justify-start px-4',
                                isSmallScreen ? 'gap-y-2' : 'gap-y-2',
                            )}
                        >
                            {navItems.map(item => {
                                const parsedLink = item?.link.split('?')[0]
                                const isActiveRoute = window.location?.pathname?.includes(
                                    parsedLink,
                                )

                                return (
                                    <NavLink
                                        className={clsx(
                                            'cursor-pointer rounded-xl flex flex-row items-center w-full',
                                            isActiveRoute &&
                                                (isOpen ||
                                                    isHover ||
                                                    isMobile) &&
                                                `bg-black60`,
                                            isOpen || isMobile
                                                ? 'py-2 px-4 justify-start'
                                                : isHover
                                                ? 'my-0 w-auto h-auto rounded-xl px-4 py-2 justify-start'
                                                : 'justify-center mx-auto w-auto h-12 rounded-full',
                                            item?.isDisable &&
                                                'pointer-events-none',
                                        )}
                                        key={item?.link}
                                        to={item?.isDisable ? '#' : item?.link}
                                        onClick={() => {
                                            removeStepsAndRequests()
                                        }}
                                    >
                                        <div
                                            className={clsx(
                                                'transition-all rounded-full flex items-center justify-center',
                                                isSmallScreen
                                                    ? 'w-8 h-8'
                                                    : 'w-8 h-8',
                                                isActiveRoute
                                                    ? 'bg-yellow'
                                                    : 'bg-black60',
                                            )}
                                        >
                                            {item?.icon(
                                                clsx(
                                                    'transition-all w-4 h-4',
                                                    isActiveRoute
                                                        ? 'text-black70'
                                                        : 'text-white',
                                                    item?.isDisable &&
                                                        'text-opacity-30',
                                                ),
                                            )}
                                        </div>
                                        <p
                                            className={clsx(
                                                'text-sm text-white font-normal ml-2 overflow-ellipsis whitespace-nowrap',
                                                !isOpen &&
                                                    !isMobile &&
                                                    isDesktop &&
                                                    'flex',
                                                !isOpen &&
                                                    !isHover &&
                                                    isDesktop &&
                                                    'hidden',
                                                isMobile && 'flex',
                                                item?.isDisable &&
                                                    'text-opacity-30',
                                            )}
                                        >
                                            {item?.name}
                                        </p>
                                        {item?.name === 'Support' &&
                                            (isOpen || isExpanded) &&
                                            !pwaIsInstalled && (
                                                <img
                                                    className={clsx('ml-auto')}
                                                    alt="New Feature Badge"
                                                    src={NewFeatureBadge}
                                                />
                                            )}
                                    </NavLink>
                                )
                            })}
                        </nav>

                        {/* Offline sidebar message, when is desktop check isOpen, when isMobile check isExpanded */}
                        {isOffline && (
                            <div
                                className={clsx(
                                    'w-full h-15 my-5 transition-all',
                                    isOpen || isHover || isExpanded
                                        ? 'px-4'
                                        : 'px-6',
                                )}
                            >
                                <div className="w-full h-11 bg-white rounded-lg">
                                    <div className="w-full h-11 flex flex-row items-center justify-center p-2 gap-1 bg-yellowLight border rounded-lg border-yellowBorder">
                                        <Warning />
                                        {(isOpen ||
                                            isHover ||
                                            (isExpanded && isMobile)) && (
                                            <p className="w-full text-xs font-normal text-textDarkerGray">
                                                You will be able to access all
                                                features once you&apos;re back
                                                online.
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Leaving this commented if we need to show it again some time in the future */}
                        {/* {isDesktop && !isSmallScreen && (
                            <div
                                className={clsx(
                                    ' mx-4 flex-col border bg-black60 border-black50 items-start justify-start p-4 rounded-xl gap-y-2',
                                    isOpen && 'md:flex hidden',
                                    !isOpen && !isHover
                                        ? 'opacity-0'
                                        : 'flex h-auto opacity-100',
                                )}
                            >
                                <p
                                    className={clsx(
                                        !isOpen && !isHover
                                            ? 'invisible overflow-ellipsis whitespace-nowrap'
                                            : 'visible',
                                        'text-white font-normal text-base transition-all',
                                    )}
                                >
                                    We’ve redesigned <br /> LiftOne Now!
                                </p>
                                <p
                                    className={clsx(
                                        'text-black30 font-normal text-sm',
                                        !isOpen && !isHover
                                            ? 'invisible transition-all'
                                            : 'block visible',
                                    )}
                                >
                                    Having issues finding what you’re looking
                                    for?
                                </p>
                                <p
                                    className={clsx(
                                        'text-yellow cursor-pointer font-normal text-sm hover:underline overflow-ellipsis whitespace-nowrap',
                                        !isOpen && !isHover
                                            ? 'invisible transition-all'
                                            : 'block visible',
                                    )}
                                    onClick={() =>
                                        (window.location =
                                            'mailto:help@liftonenow.com')
                                    }
                                >
                                    Contact Support
                                </p>
                            </div>
                        )} */}

                        <NavLink
                            className={clsx(
                                'w-full h-auto flex flex-row items-center bg-black60',
                                isSmallScreen ? 'p-4' : 'p-4',
                                !isOpen && !isHover && isDesktop
                                    ? 'justify-center'
                                    : 'justify-start',
                                isMobile && 'justify-start',
                            )}
                            to={AppRoutes.profile()}
                        >
                            <div
                                className={clsx(
                                    isInProfilePage
                                        ? 'bg-yellow'
                                        : 'bg-black50',
                                    'w-10 h-10 min-w-10 rounded-full  flex items-center justify-center overflow-hidden',
                                )}
                            >
                                <p
                                    className={clsx(
                                        'font-medium text-base',
                                        isInProfilePage
                                            ? 'text-black'
                                            : 'text-white ',
                                    )}
                                >
                                    {acronymName}
                                </p>
                            </div>
                            <div
                                className={clsx(
                                    'flex flex-col items-start justify-start ml-2 cursor-pointer max-w-full overflow-hidden',
                                    !isOpen && !isMobile && !isHover
                                        ? 'hidden'
                                        : 'flex',
                                )}
                            >
                                {isImpersonated && (
                                    <p className="text-yellow text-xs max-w-full truncate font-normal">
                                        You are viewing as
                                    </p>
                                )}
                                <p className="font-normal text-base text-white truncate max-w-full">
                                    {firstName} {lastName}
                                </p>
                                <p className="text-black30 text-sm max-w-full truncate font-normal">
                                    {userCompanyName}
                                </p>
                            </div>
                        </NavLink>
                    </div>
                )}
            </div>
        </ClickAwayListener>
    )
}

export default Sidebar
